import { InfoIcon, Undo2Icon } from "lucide-react";
import {
  createContext,
  type ComponentPropsWithoutRef,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { cn } from "../../lib/utils";
import { WorkspacePageContainer, type WorkspacePageWidth } from "../WorkspacePageContainer";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import type { ProjectScopedServerSettingKey } from "@afar-research/afar";
import { useClearScopedSettings, useScopedSettingSource } from "./useScopedSettings";

const EMPTY_SETTING_KEYS: readonly ProjectScopedServerSettingKey[] = [];

interface SettingsSearchTargetContextValue {
  readonly targetId: string | null;
  readonly onTargetHandled: () => void;
}

const noop = () => undefined;
const SettingsSearchTargetContext = createContext<SettingsSearchTargetContextValue>({
  targetId: null,
  onTargetHandled: noop,
});

export function SettingsSearchTargetProvider({
  targetId,
  onTargetHandled = noop,
  children,
}: {
  readonly targetId: string | null;
  readonly onTargetHandled?: () => void;
  readonly children: ReactNode;
}) {
  const value = useMemo(() => ({ targetId, onTargetHandled }), [onTargetHandled, targetId]);
  return <SettingsSearchTargetContext value={value}>{children}</SettingsSearchTargetContext>;
}

function scrollAndFocusSettingsTarget(target: HTMLElement, highlight = true): void {
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // A section's own box is taller than the part worth centring, so it marks
  // the piece to scroll to; anything unmarked scrolls itself.
  const markedScrollTarget =
    typeof target.querySelector === "function"
      ? target.querySelector<HTMLElement>(":scope > [data-settings-scroll-target]")
      : null;
  const scrollTarget =
    markedScrollTarget ??
    (target.tagName === "SECTION" && target.firstElementChild
      ? (target.firstElementChild as HTMLElement)
      : target);
  scrollTarget.scrollIntoView({
    behavior: prefersReducedMotion ? "auto" : "smooth",
    block: "center",
  });
  target.focus({ preventScroll: true });
  target.classList.remove("settings-search-target-pulse");
  if (!highlight || prefersReducedMotion) return;
  void target.offsetWidth;
  target.classList.add("settings-search-target-pulse");
  // The class also suppresses the focus outline (the pulse is the destination
  // indicator), so drop it once the element is no longer the destination.
  target.addEventListener("blur", () => target.classList.remove("settings-search-target-pulse"), {
    once: true,
  });
}

function useSettingsSearchTarget<T extends HTMLElement>(id: string | undefined) {
  const { targetId, onTargetHandled } = useContext(SettingsSearchTargetContext);
  const isSearchTarget = id !== undefined && id === targetId;
  return useCallback(
    (target: T | null) => {
      if (!target || !isSearchTarget) return;
      scrollAndFocusSettingsTarget(target);
      onTargetHandled();
    },
    [isSearchTarget, onTargetHandled],
  );
}

/**
 * A settings control that is not a whole row but still has to be reachable
 * from search — a field nested inside an expandable row, say. Scrolls and
 * focuses itself when search names its id.
 */
export function SettingsSearchTarget({
  id,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"div"> & { id: string }) {
  const targetRef = useSettingsSearchTarget<HTMLDivElement>(id);
  return (
    <div {...props} id={id} ref={targetRef} tabIndex={-1} className={className}>
      {children}
    </div>
  );
}

/** Re-render every `intervalMs`; return a stable timestamp snapshot for render-time relative labels. */
export function useSettingsSearchTargetId(): string | null {
  return useContext(SettingsSearchTargetContext).targetId;
}

export function useRelativeTimeTick(intervalMs = 1_000) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return nowMs;
}

/** Muted section headings have no descriptions; explanatory copy belongs to individual settings. */
export function SettingsSection({
  title,
  hideTitle = false,
  icon,
  headerAction,
  variant = "grouped",
  children,
  className,
  ...sectionProps
}: ComponentPropsWithoutRef<"section"> & {
  title: string;
  hideTitle?: boolean;
  icon?: ReactNode;
  headerAction?: ReactNode;
  variant?: "grouped" | "plain";
  children: ReactNode;
}) {
  const targetRef = useSettingsSearchTarget<HTMLElement>(sectionProps.id);
  return (
    <section
      {...sectionProps}
      ref={targetRef}
      tabIndex={sectionProps.id ? -1 : sectionProps.tabIndex}
      className={cn(!hideTitle && "space-y-2.5", className)}
    >
      {hideTitle ? (
        <h2 className="sr-only">{title}</h2>
      ) : (
        <div
          data-settings-scroll-target
          className="flex min-h-7 items-start justify-between gap-4 px-3 sm:px-4"
        >
          <div className="min-w-0">
            <h2 className="flex min-h-7 items-center gap-2 text-sm font-normal tracking-[-0.005em] text-foreground/70">
              {icon}
              {title}
            </h2>
          </div>
          <div className="flex min-h-7 min-w-7 items-center justify-end">{headerAction}</div>
        </div>
      )}
      <div
        data-settings-scroll-target={hideTitle ? "" : undefined}
        className={cn(
          "relative overflow-visible text-foreground",
          variant === "grouped"
            ? "rounded-xl border border-border/60 bg-card/40 shadow-xs/5 [&>*+*]:border-t [&>*+*]:border-border/50 [&>[data-slot=settings-row]]:rounded-none"
            : "space-y-1",
        )}
      >
        {children}
      </div>
    </section>
  );
}

/** Wraps a group of rows the current client cannot act on, with the reason. */
export function SettingsUnavailableGroup({
  children,
  message,
}: {
  children: ReactNode;
  message?: ReactNode;
}) {
  if (message === undefined) return children;

  return (
    <div className="border-border/60 bg-muted/20 py-1.5">
      <div className="flex items-start gap-2 px-3 py-2 text-[12px] leading-relaxed text-muted-foreground sm:px-4">
        <InfoIcon className="mt-0.5 size-3.5 shrink-0 text-warning" />
        <p>{message}</p>
      </div>
      <div className="[&_h3]:opacity-64 [&_p]:opacity-64">{children}</div>
    </div>
  );
}

/**
 * One setting.
 *
 * `settingKeys` names the keys this row writes. At a project scope that is
 * what lets the row say whether the value is the project's own answer or its
 * Environment's, and offer to stop overriding. A row that names no keys is
 * simply not part of that chain — most rows are Environment-wide and need
 * nothing here.
 */
export function SettingsRow({
  title,
  description,
  status,
  resetAction,
  control,
  settingKeys = EMPTY_SETTING_KEYS,
  children,
  className,
  ...rowProps
}: Omit<ComponentPropsWithoutRef<"div">, "title"> & {
  title: ReactNode;
  description?: ReactNode;
  status?: ReactNode;
  resetAction?: ReactNode;
  control?: ReactNode;
  settingKeys?: readonly ProjectScopedServerSettingKey[];
  children?: ReactNode;
}) {
  const targetRef = useSettingsSearchTarget<HTMLDivElement>(rowProps.id);
  const source = useScopedSettingSource(settingKeys);
  const clearOverrides = useClearScopedSettings();
  return (
    <div
      {...rowProps}
      ref={targetRef}
      tabIndex={rowProps.id ? -1 : rowProps.tabIndex}
      data-slot="settings-row"
      className={cn(
        "@container/settings-row rounded-xl px-3 aria-disabled:opacity-50 sm:px-4 aria-disabled:[&_*]:text-muted-foreground",
        children ? "pt-3 pb-1" : "py-3",
        className,
      )}
    >
      <div className="flex flex-col gap-3 @min-[32rem]/settings-row:grid @min-[32rem]/settings-row:grid-cols-[minmax(0,1fr)_minmax(10rem,auto)] @min-[32rem]/settings-row:items-center @min-[32rem]/settings-row:gap-8">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-h-5 items-center gap-1.5">
            <h3 className="text-sm font-medium tracking-[-0.005em] text-foreground">{title}</h3>
            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
              {resetAction}
            </span>
            {settingKeys.length > 0 && source !== "inherited" ? (
              <button
                type="button"
                data-testid="settings-row-override"
                onClick={() => clearOverrides(settingKeys)}
                title="This project answers this for itself. Use the environment's value instead."
                className="cursor-pointer rounded bg-primary/15 px-1.5 text-[10px] font-medium text-primary hover:bg-primary/25"
              >
                {source === "mixed" ? "Mixed" : "Overridden"}
              </button>
            ) : null}
          </div>
          {description ? (
            <p className="max-w-xl text-[13px] leading-[1.45] text-muted-foreground/80">
              {description}
            </p>
          ) : null}
          {status ? <div className="pt-0.5 text-xs text-muted-foreground">{status}</div> : null}
        </div>
        {control ? (
          <div className="flex w-full min-w-0 shrink-0 items-center gap-2 @min-[32rem]/settings-row:w-auto @min-[32rem]/settings-row:justify-end">
            {control}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export function SettingResetButton({
  label,
  tooltip = "Reset to default",
  disabled = false,
  onClick,
}: {
  label: string;
  tooltip?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="icon-micro"
            variant="ghost-muted"
            aria-label={`Reset ${label} to default`}
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              onClick();
            }}
          >
            <Undo2Icon className="size-3" />
          </Button>
        }
      />
      <TooltipPopup side="top">{tooltip}</TooltipPopup>
    </Tooltip>
  );
}

export function SettingsPageContainer({
  children,
  className,
  id,
  width = "readable",
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  width?: WorkspacePageWidth;
}) {
  const targetRef = useSettingsSearchTarget<HTMLDivElement>(id);
  return (
    <div className="scrollbar-gutter-both min-h-0 flex-1 overflow-y-auto" data-settings-page-scroll>
      <WorkspacePageContainer
        ref={targetRef}
        id={id}
        tabIndex={id ? -1 : undefined}
        width={width}
        className={cn("gap-8", className)}
      >
        {children}
      </WorkspacePageContainer>
    </div>
  );
}

export function scrollToSettingsTarget(targetId: string): boolean {
  const target = document.getElementById(targetId);
  if (!target) return false;
  scrollAndFocusSettingsTarget(target);
  return true;
}

export function EnvironmentSettingsScopeSection({
  scope,
  remoteDescription,
}: {
  readonly scope: {
    readonly label: string;
    readonly canEditHostSettings: boolean;
  } | null;
  readonly remoteDescription: string;
}) {
  if (scope?.canEditHostSettings) return null;
  return (
    <SettingsSection title="Environment">
      <SettingsRow
        title={scope?.label ?? "No connected environment"}
        description={
          scope === null
            ? "This client is not connected to an Environment Service."
            : scope.canEditHostSettings
              ? "This computer's Environment. Local access remains available independently of AFAR Connect."
              : remoteDescription
        }
        status={
          scope === null
            ? null
            : scope.canEditHostSettings
              ? "Local Environment"
              : "Remote Environment"
        }
      />
    </SettingsSection>
  );
}
